import { createContext, useContext, useEffect, useState } from "react";
import { api, setAuthToken } from "./api";
import { supabase } from "./supabase";

const AuthCtx = createContext(null);

/**
 * Swap the document favicon to the org's logo_url when one is set,
 * otherwise keep the bundled default (`/favicon.png`). Org call is
 * fire-and-forget — a failure quietly leaves the default in place.
 */
const DEFAULT_FAVICON = `${process.env.PUBLIC_URL || ""}/favicon.png`;
// Only swap the main/apple-touch icons to the org logo — keep `favicon-32`
// pointing at the bundled small PNG since browsers prefer the smallest
// available size for the tab strip.
const applyFavicon = (href) => {
  ["favicon-main", "favicon-apple"].forEach((id) => {
    const link = document.getElementById(id);
    if (link) link.href = href;
  });
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null=loading, false=unauth, obj=auth
  const [session, setSession] = useState(null);

  useEffect(() => {
    let mounted = true;
    // Watchdog: if Supabase's session restore / /auth/me hangs (e.g. stale refresh
    // token, network blip), fail open after 6s so the app stops showing "Loading…"
    // forever — the user lands on /login and can sign in again.
    const watchdog = setTimeout(() => {
      if (mounted) setUser(prev => prev === null ? false : prev);
    }, 6000);

    const init = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!mounted) return;
        setSession(data.session);
        if (data.session) {
          try {
            const r = await api.get("/auth/me");
            if (mounted) setUser(r.data);
          } catch (e) {
            // 401/403 → no profile yet, try bootstrap once; otherwise fall back to /login
            if (e?.response?.status === 403 || e?.response?.status === 401) {
              try {
                await api.post("/auth/bootstrap", {});
                const r2 = await api.get("/auth/me");
                if (mounted) setUser(r2.data);
              } catch { if (mounted) setUser(false); }
            } else if (mounted) setUser(false);
          }
        } else if (mounted) setUser(false);
      } catch {
        // Supabase getSession threw (corrupted localStorage, network) — escape to login.
        if (mounted) {
          try { await supabase.auth.signOut(); } catch { /* ignore */ }
          setUser(false);
        }
      } finally {
        clearTimeout(watchdog);
      }
    };
    init();
    const { data: sub } = supabase.auth.onAuthStateChange(async (event, s) => {
      setSession(s);
      if (!s) { setUser(false); return; }
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        try { const r = await api.get("/auth/me"); setUser(r.data); }
        catch (e) {
          if (e?.response?.status === 403 || e?.response?.status === 401) {
            try { await api.post("/auth/bootstrap", {}); const r2 = await api.get("/auth/me"); setUser(r2.data); }
            catch { setUser(false); }
          }
        }
      }
    });
    return () => { mounted = false; clearTimeout(watchdog); sub.subscription.unsubscribe(); };
  }, []);

  // Favicon swap: once we have an authed user, ask /company for the org's
  // logo_url. If present, use it as the favicon; otherwise leave the default.
  // On logout / unauth, restore the default.
  useEffect(() => {
    if (!user || user === false) { applyFavicon(DEFAULT_FAVICON); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/company");
        if (cancelled) return;
        applyFavicon(r.data?.logo_url || DEFAULT_FAVICON);
      } catch {
        if (!cancelled) applyFavicon(DEFAULT_FAVICON);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Apply theme class to <html> so the entire app re-themes when the user
  // toggles dark/light. Persists across reloads via the server-side `theme`
  // column on the user row (so each device/session reflects the latest
  // choice without needing localStorage).
  useEffect(() => {
    const html = document.documentElement;
    if (user && user.theme === "dark") html.classList.add("dark");
    else html.classList.remove("dark");
  }, [user]);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    setAuthToken(data.session?.access_token);
    setSession(data.session);
    const r = await api.get("/auth/me");
    setUser(r.data);
    return r.data;
  };

  const register = async ({ email, password, name, company_name }) => {
    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: { data: { name, company_name } },
    });
    if (error) throw error;
    if (!data.session) {
      throw new Error("Account created — please check your email to confirm before signing in.");
    }
    setAuthToken(data.session?.access_token);
    setSession(data.session);
    await api.post("/auth/bootstrap", {});
    const r = await api.get("/auth/me");
    setUser(r.data);
    return r.data;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setAuthToken(null);
    setSession(null);
    setUser(false);
  };

  // iter 73.4 — Inactivity auto-logout (client-side, 4h).
  //
  // Only ARMED when the user is authenticated (object). On every
  // user-input event we reset a 4-hour timer; when it elapses we sign
  // the user out, push a flash message into sessionStorage (Login.jsx
  // already reads `idle_logout_msg`), and redirect to /login.
  //
  // Pairs naturally with the iter 73.2 remember-me: returning users
  // only need to type their password — email is pre-filled.
  //
  // Uses `passive: true` listeners and a single shared timer so the
  // overhead per scroll/move event is a single Date.now() comparison.
  useEffect(() => {
    if (!user || user === false) return undefined; // not signed in → no-op

    const IDLE_MS = 4 * 60 * 60 * 1000; // 4 hours
    let timer = null;

    const onIdle = async () => {
      // Write the flash message FIRST — Login.jsx reads + clears it on
      // mount. Important: set this BEFORE any state changes, because
      // `setUser(false)` would trigger a synchronous React-router redirect
      // to /login that consumes the message in the same tick, BEFORE the
      // hard reload below mounts a fresh Login. So we deliberately do NOT
      // call setUser(false) here — the hard reload reinitialises everything
      // cleanly (the supabase signOut has already cleared the session).
      try {
        sessionStorage.setItem(
          "idle_logout_msg",
          "You were signed out after 4 hours of inactivity. Please sign in again.",
        );
      } catch { /* storage blocked — ignore */ }
      try { await supabase.auth.signOut(); } catch { /* ignore */ }
      // Hard reload to /login regardless of current route. This also tears
      // down any in-flight fetches / WebSocket subscriptions cleanly.
      window.location.href = "/login";
    };

    const arm = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(onIdle, IDLE_MS);
    };

    // Any of these resets the timer. Passive listeners avoid
    // scroll-jank. `visibilitychange` re-arms when the tab regains
    // focus so a tab left open in the background doesn't fire instantly
    // on return.
    const EVENTS = ["mousedown", "keydown", "scroll", "touchstart", "visibilitychange"];
    EVENTS.forEach((ev) => window.addEventListener(ev, arm, { passive: true }));
    arm(); // initial arm

    return () => {
      if (timer) clearTimeout(timer);
      EVENTS.forEach((ev) => window.removeEventListener(ev, arm));
    };
  }, [user]);

  // Iter 27: imperative refresh of /auth/me — used by UpgradeSuccess to
  // pick up the new org_type=core immediately after the Stripe webhook /
  // sync handler flips it.
  const refresh = async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      return r.data;
    } catch { return null; }
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
    if (error) throw error;
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, session, login, register, logout, signInWithGoogle, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
